/**
 * Flickity for the product gallery.
 *
 * The gallery markup in "snippets/product-gallery.liquid" can be rendered in Flickity mode. That mode replaces the
 * native <scroll-carousel> with a plain container, which means the carousel API that theme.js expects is gone:
 * ProductGallery calls select(), filter(), cells, selectedCell and isScrollable on it and listens for
 * "carousel:change" / "carousel:settle". Without those, zoom, the variant to image jump and the color filter break.
 *
 * This script therefore does two things:
 *
 *   1. it installs a ScrollCarousel compatible adapter on the container, which works with or without Flickity, and
 *   2. it initialises Flickity on top of it, including for content that is injected after page load.
 *
 * The card slider ("assets/product-card-slider.js") cannot be reused here: its selector is scoped to
 * ".product-card .main-carousel", and the gallery is not a product card.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 999px)';
  const CELL_SELECTOR = '.carousel-cell:not([hidden])';

  const loader = document.querySelector('script[data-product-card-slider]');
  const flickityScript = loader?.dataset.flickityScript;
  const imagesLoadedScript = loader?.dataset.imagesLoadedScript;
  const flickityStyle = loader?.dataset.flickityStyle;

  let assetsPromise = null;

  function loadStyle(url) {
    if (!url || document.querySelector('link[data-bans-flickity-style]')) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.dataset.bansFlickityStyle = 'true';
    document.head.appendChild(link);
  }

  function loadScript(url, marker) {
    if (!url) return Promise.resolve();

    const existing = document.querySelector(`script[data-bans-carousel-asset="${marker}"]`);

    if (existing) {
      if (existing.dataset.loaded === 'true') return Promise.resolve();

      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.dataset.bansCarouselAsset = marker;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function ensureAssets() {
    if (typeof window.Flickity === 'function') return Promise.resolve();

    if (!assetsPromise) {
      loadStyle(flickityStyle);
      assetsPromise = loadScript(imagesLoadedScript, 'images-loaded')
        .then(() => loadScript(flickityScript, 'flickity'))
        .catch((error) => {
          assetsPromise = null;
          throw error;
        });
    }

    return assetsPromise;
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * ADAPTER
   * -------------------------------------------------------------------------------------------------------------------
   *
   * Mirrors the public API of ScrollCarousel (see "js/common/carousel/scroll-carousel.js" in theme.js) so that
   * ProductGallery, the thumbnail navigation and the page dots keep working. When Flickity is active the calls are
   * delegated to it, otherwise the container behaves like a plain scroll container.
   */
  function installAdapter(carousel) {
    if (carousel.bansAdapterInstalled) return;
    carousel.bansAdapterInstalled = true;

    let fallbackIndex = 0;

    // Not ":scope >" — Flickity moves the cells into its own .flickity-viewport > .flickity-slider wrapper,
    // so after initialisation they are no longer direct children of the container.
    const allCells = () => Array.from(carousel.querySelectorAll('.carousel-cell'));

    /**
     * The rendered order, captured before Flickity ever touches the DOM.
     *
     * Flickity only wraps the cells its cellSelector matches, so hidden cells stay outside the viewport. On destroy
     * it appends the wrapped ones back, which lands them after the cells that were left behind — the gallery order
     * would silently scramble every time the color filter is applied and cleared.
     */
    const originalOrder = allCells();
    carousel.bansRestoreOrder = () => originalOrder.forEach((cell) => carousel.appendChild(cell));
    const cells = () => allCells().filter((cell) => !cell.hasAttribute('hidden'));

    const emit = (name, detail) => carousel.dispatchEvent(new CustomEvent(name, { detail }));

    const emitEdges = (index) => {
      const last = cells().length - 1;
      emit(index <= 0 ? 'scroll:edge-nearing' : 'scroll:edge-leaving', { position: 'start' });
      emit(index >= last ? 'scroll:edge-nearing' : 'scroll:edge-leaving', { position: 'end' });
    };

    Object.defineProperties(carousel, {
      allCells: { get: allCells, configurable: true },
      cells: { get: cells, configurable: true },
      selectedIndex: {
        get() {
          return carousel.flickity ? carousel.flickity.selectedIndex : fallbackIndex;
        },
        configurable: true
      },
      selectedCell: {
        get() {
          return cells()[carousel.selectedIndex];
        },
        configurable: true
      },
      isScrollable: {
        get() {
          if (carousel.flickity) return cells().length > 1;
          return carousel.scrollWidth - carousel.clientWidth > 1 || carousel.scrollHeight - carousel.clientHeight > 1;
        },
        configurable: true
      }
    });

    carousel.select = function (index, { instant = false } = {}) {
      const list = cells();
      if (!(index in list)) return;

      emit('carousel:select', { index, cell: list[index] });

      if (carousel.flickity) {
        carousel.flickity.select(index, false, instant);
        return;
      }

      fallbackIndex = index;
      list[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: instant ? 'auto' : 'smooth' });
      emit('carousel:change', { index, cell: list[index] });
      emit('carousel:settle', { index, cell: list[index] });
      emitEdges(index);
    };

    carousel.previous = function ({ instant = false } = {}) {
      carousel.select(Math.max(carousel.selectedIndex - 1, 0), { instant });
    };

    carousel.next = function ({ instant = false } = {}) {
      carousel.select(Math.min(carousel.selectedIndex + 1, cells().length - 1), { instant });
    };

    /**
     * Filtering works through the "hidden" attribute, matching ScrollCarousel. Flickity has no concept of hidden
     * cells, so it is rebuilt afterwards. Its cellSelector ignores hidden cells, which keeps the Flickity indexes
     * aligned with the visible cells the rest of the theme counts.
     */
    carousel.filter = function (indexes = []) {
      allCells().forEach((cell, index) => cell.toggleAttribute('hidden', indexes.includes(index)));
      fallbackIndex = 0;

      if (carousel.flickity) {
        destroyFlickity(carousel);
        initFlickity(carousel);
      }

      emit('carousel:filter', { filteredIndexes: indexes });
    };

    carousel.bansEmitEdges = emitEdges;
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * FLICKITY LIFECYCLE
   * -------------------------------------------------------------------------------------------------------------------
   */
  function flickityOptions(carousel) {
    let options = {};

    try {
      options = JSON.parse(carousel.getAttribute('data-bans-flickity') || '{}');
    } catch (error) {
      options = {};
    }

    return Object.assign({
      cellAlign: 'left',
      contain: true,
      prevNextButtons: true,
      pageDots: false,
      imagesLoaded: true,
      // Same thin chevron the product card sliders use ("assets/product-card-slider.js"), otherwise the gallery
      // arrows are Flickity's default fat ones and the modal looks nothing like the collection page
      arrowShape: 'M 65,20 L 35,50 L 65,80'
    }, options, {
      cellSelector: CELL_SELECTOR,
      // The gallery keeps its own accessible labels on the cells
      accessibility: true
    });
  }

  /**
   * Flickity keeps its instances in Flickity.data(element), our own bookkeeping in carousel.flickity. Anything that
   * constructs an instance without going through this file — Flickity's htmlInit() on any [data-flickity] element, a
   * theme script — is invisible to destroyFlickity() and can never be torn down. Adopting it here closes that gap.
   */
  function adoptExternalInstance(carousel) {
    if (carousel.flickity || typeof window.Flickity?.data !== 'function') return;

    const existing = window.Flickity.data(carousel);
    if (existing) carousel.flickity = existing;
  }

  function initFlickity(carousel) {
    adoptExternalInstance(carousel);

    if (carousel.flickity || typeof window.Flickity !== 'function') return;
    // Re-checked here and not only in sync(): filter() also calls this, and a mobile-only gallery must never end up
    // with Flickity on desktop, where the theme grid is supposed to lay the media out
    if (!isEnabledForViewport(carousel)) return;
    if (carousel.querySelectorAll(CELL_SELECTOR).length < 2) return;

    const initialIndex = Math.max(0, carousel.cells.findIndex((cell) => cell.classList.contains('is-initial')));
    const options = flickityOptions(carousel);

    if (initialIndex > 0) options.initialIndex = initialIndex;

    const flkty = new window.Flickity(carousel, options);
    carousel.flickity = flkty;

    // Flickity's own arrows say "Previous" and "Next"; the rest of the shop speaks German
    if (carousel.dataset.previousLabel && flkty.prevButton) flkty.prevButton.element.setAttribute('aria-label', carousel.dataset.previousLabel);
    if (carousel.dataset.nextLabel && flkty.nextButton) flkty.nextButton.element.setAttribute('aria-label', carousel.dataset.nextLabel);

    flkty.on('change', (index) => {
      carousel.dispatchEvent(new CustomEvent('carousel:change', { detail: { index, cell: carousel.cells[index] } }));
      carousel.bansEmitEdges?.(index);
    });

    flkty.on('settle', (index) => {
      carousel.dispatchEvent(new CustomEvent('carousel:settle', { detail: { index, cell: carousel.cells[index] } }));
    });

    // The modal animates in, so the initial measurement can be taken before the final width is known
    requestAnimationFrame(() => flkty.resize());
    carousel.addEventListener('load', () => flkty.resize(), true);
    carousel.bansEmitEdges?.(flkty.selectedIndex);
  }

  function destroyFlickity(carousel) {
    adoptExternalInstance(carousel);

    if (!carousel.flickity) return;

    carousel.flickity.destroy();
    carousel.flickity = null;
    carousel.bansRestoreOrder?.();
  }

  function isEnabledForViewport(carousel) {
    if (carousel.dataset.flickityMode !== 'mobile') return true;
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getCarousels(root) {
    const found = [];
    const selector = 'product-gallery .product-gallery__carousel[data-bans-flickity]';

    if (root instanceof Element && root.matches(selector)) found.push(root);
    root.querySelectorAll?.(selector).forEach((carousel) => {
      if (!found.includes(carousel)) found.push(carousel);
    });

    return found;
  }

  async function sync(root) {
    const carousels = getCarousels(root || document);
    if (!carousels.length) return;

    carousels.forEach(installAdapter);

    const wanted = carousels.filter(isEnabledForViewport);
    carousels.filter((carousel) => !isEnabledForViewport(carousel)).forEach(destroyFlickity);

    if (!wanted.length) return;

    try {
      await ensureAssets();
    } catch (error) {
      // Without Flickity the adapter still provides a usable scroll container
      return;
    }

    wanted.forEach(initFlickity);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync(document), { once: true });
  } else {
    sync(document);
  }

  document.addEventListener('shopify:section:load', (event) => sync(event.target));
  document.addEventListener('theme:loading:end', () => sync(document));
  // Capture phase on purpose: theme.js dispatches "dialog:after-show" without "bubbles", so a bubble-phase
  // listener on document never runs. The quick buy modal injects its gallery after "theme:loading:end" has
  // already fired, which makes this the only hook that sees it.
  document.addEventListener('dialog:after-show', (event) => sync(event.target), true);
  window.matchMedia(MOBILE_QUERY).addEventListener('change', () => sync(document));
})();
